import clsx from 'clsx'
import { useState } from 'react'

export interface MultiSelectChipOption {
  id: string
  label: string
  count?: number
}

export interface MultiSelectChipsProps {
  options: MultiSelectChipOption[]
  /** Selected ids. EMPTY MEANS ALL -- the "nothing narrowed" state, same as the API's
   *  NULL list, so a fresh filter sends no list at all rather than every value. */
  value: string[]
  onChange: (next: string[]) => void
  /** Label of the leading chip that clears the selection. */
  allLabel?: string
  /** Show at most this many options before a "+N more" toggle; the selected ones are
   *  always shown so a hidden pick can't silently stay active. */
  max?: number
  /** Accessible group name, e.g. "Alert type". */
  label: string
}

/**
 * Multi-select sibling of FilterChips (same pill recipe, same focus ring), for the alert
 * kind / severity pickers where "CRITICAL and WARN" is a normal question. Each chip is
 * `aria-pressed`, the group is `role="group"`, and the leading "All" chip is the only
 * way to clear -- deselecting the last picked chip ALSO lands on "all", which is the
 * same state, so there is no dead "none selected" pose.
 */
export function MultiSelectChips({
  options, value, onChange, allLabel = 'All', max, label,
}: MultiSelectChipsProps): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const selected = new Set(value)
  const limit = max && !expanded ? max : options.length
  const visible = options.filter((o, i) => i < limit || selected.has(o.id))
  const hidden = options.length - visible.length
  const allActive = selected.size === 0

  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    // Keep the caller's option order so the URL/API list is stable for the same picks.
    onChange(options.map((o) => o.id).filter((oid) => next.has(oid)))
  }

  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      <Chip active={allActive} onClick={() => onChange([])} label={allLabel} />
      {visible.map((o) => (
        <Chip key={o.id} active={selected.has(o.id)} onClick={() => toggle(o.id)} label={o.label} count={o.count} />
      ))}
      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex h-7 items-center rounded-full px-2.5 text-[11px] font-medium text-[#2563EB] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
        >
          +{hidden} more
        </button>
      ) : max && expanded && options.length > max ? (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="flex h-7 items-center rounded-full px-2.5 text-[11px] font-medium text-[#64748B] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
        >
          fewer
        </button>
      ) : null}
    </div>
  )
}

function Chip({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count?: number }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={clsx(
        'flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-1',
        active
          ? 'bg-[#0F172A] text-white'
          : 'border border-[rgba(15,23,42,0.07)] bg-white text-[#334155] hover:border-[rgba(15,23,42,0.16)]',
      )}
    >
      {label}
      {count != null ? (
        <span className={clsx('font-mono text-[10.5px] tabular-nums', active ? 'text-white/60' : 'text-[#94A3B8]')}>
          {count.toLocaleString('en-IN')}
        </span>
      ) : null}
    </button>
  )
}
